/**
 * Pilihan tema terang, gelap, atau ikut sistem.
 *
 * Nilai disimpan di localStorage dengan kunci yang sama seperti `public/theme.js`,
 * yang memasang atribut `data-theme` sebelum React dirender supaya halaman tidak
 * berkedip terang. Modul ini menambahkan pengikatan ke perubahan
 * `prefers-color-scheme` dan memakai ulang berkas pra-render sebagai satu-satunya
 * tempat logika resolusi ditulis.
 */

export type ThemeChoice = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'tahupay.theme'

export const THEME_CHOICES: ThemeChoice[] = ['light', 'dark', 'system']

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === 'light' || value === 'dark' || value === 'system'
}

/** Pilihan tersimpan, atau 'system' bila belum pernah dipilih. */
export function readThemeChoice(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    return isThemeChoice(stored) ? stored : 'system'
  } catch {
    return 'system'
  }
}

export function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

/** Tema yang benar-benar berlaku untuk sebuah pilihan. */
export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  if (choice === 'system') {
    return prefersDark() ? 'dark' : 'light'
  }
  return choice
}

/** Menerapkan tema dan menyimpan pilihannya. */
export function applyTheme(choice: ThemeChoice): ResolvedTheme {
  const resolved = resolveTheme(choice)
  const root = document.documentElement
  root.setAttribute('data-theme', resolved)
  root.setAttribute('data-theme-choice', choice)
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, choice)
  } catch {
    // Penyimpanan bisa dilarang di mode privat; tema tetap berlaku untuk sesi ini.
  }
  return resolved
}

/** Tema yang sedang berlaku, dibaca dari atribut yang dipasang pra-render. */
export function currentResolvedTheme(): ResolvedTheme {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'
}
